import React from 'react'
import { produce } from 'immer'
import { create, useStore  } from 'zustand'
import { useRequiredContext } from '../../utils/use-required-context'
import { isEditMode } from '../../utils/is-edit-mode'
import { posthog } from 'posthog-js'
import { ComponentNamespace } from '../../redux/pubsub'
import uniqueId from 'lodash.uniqueid'
import { DistributiveOmit } from '../../utils/typescript'

type Store = ReturnType<typeof createEventLogStore>

type State = {
  eventLogs: EventLog[]
  register: (item: EventLog) => void
}

export type EventLog = (
  | AppVariableUpdateEvent
  | SparqlRequestEvent
  | RuleExecutionEvent
  | TemplateResolutionEvent
)

type AppVariableUpdateEvent = {
  type: 'app-variable-update-event'
  id: string
  timestamp: string
  namespace: ComponentNamespace
  key: string
  valueOld: string
  valueNew: string
  ctx: string | undefined
}

type SparqlRequestEvent = {
  type: 'sparql-request-event'
  id: string
  timestamp: string
  namespace: ComponentNamespace
  templateId: string
  request: string
  response: string
}

type RuleExecutionEvent = {
  type: 'rule-execution-event'
  id: string
  timestamp: string
  tag: string
  params: string
}

type TemplateResolutionEvent = {
  type: 'template-resolution-event'
  id: string
  timestamp: string
  template: string
  partialResults: string
  endResult?: string
  error?: string
}

const StoreContext = React.createContext<Store | null>(null)
const useStoreInstance = () => useRequiredContext(StoreContext)

/** This expects only one event-log-store to be active */
export let storeRef: {current: Store | undefined} = {current: undefined}
const preStoreCreationBuffer: EventLog[] = []


export function createEventLogStore() {
  const store = create<State>()((set, _get, _store) => ({
    eventLogs: [],
    register: (item) => registerEventLog(item, store),
  }))

  storeRef.current = store
  return store
}

export function registerPostHog(item: EventLog) {
  posthog.capture('Wistor Event', item)
}

/**
 * If no event-log-store specified, then the last created store is used.
 * This is therefore only useful if we can assume only one event-log-store is active.
 */
export function registerEventLog(itemNoId: DistributiveOmit<EventLog, 'id'>, store?: Store): void {
  const item: EventLog = {...itemNoId, id: uniqueId()}
  registerPostHog(item)

  if (!isEditMode()) return

  const target = store ?? storeRef.current
  if (!target) {
    if (preStoreCreationBuffer.length > 1337) throw new Error('an event-log-store is supposed to exist by now')

    preStoreCreationBuffer.push(item)
    return
  }

  while (preStoreCreationBuffer.length) {
    const preStoreCreationItem = preStoreCreationBuffer.shift()
    if (!preStoreCreationItem) break

    target.setState(produce((state: State) => {
      state.eventLogs.push(preStoreCreationItem)
    }))
  }

  target.setState(produce((state: State) => {
    state.eventLogs.push(item)
  }))

}

function useEventLogStore<T>(selector: (state: State) => T) {
  const store = useStoreInstance()
  return useStore(store, selector)
}

export function useEventLogs() {
  return useEventLogStore(st => st.eventLogs)
}

export function useRegisterEventLog() {
  return useEventLogStore(st => st.register)
}

export function EventLogStoreProvider(props: {
  children: React.ReactNode
}) {
  const eventLogStoreRef = React.useRef<Store>()
  if (eventLogStoreRef.current == null) {
    eventLogStoreRef.current = createEventLogStore()
  }

  return (
    <StoreContext.Provider value={eventLogStoreRef.current}>
      {props.children}
    </StoreContext.Provider>
  )
}
